import axios from 'axios';
import { apiClient } from '../../apiHelper';
import * as url from '../../urlHelper';
import { usePaginated, useSingleFetch } from '../react-query';
import { viewFrameQueryKeys } from './view-query-keys';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { QUALITY_FLAGS } from '../../../../../../ui/src/components/MeasurementTable/Components/Report/config/planes.config';

export function usePaginatedViewSegmentationsReport(
  page: number,
  itemsPerPage: number,
  studyUID: string,
  viewName: string,
  seriesIds?: string[],
  qualityFlags?: Array<{ code: string; shouldBeVisible: boolean }>,
  isFiltered?: boolean
) {
  const buildUrl = (p: number) => {
    let u = `view-frame/${studyUID}/views/${viewName}/frames?page=${p}&page_size=${itemsPerPage}`;

    if (seriesIds && seriesIds.length > 0 && !(seriesIds.length === 1 && seriesIds[0] === 'All')) {
      u += `&series_ids=${encodeURIComponent(seriesIds.join(','))}`;
    }

    if (qualityFlags && qualityFlags.length > 0) {
      const viewQualityFlags = QUALITY_FLAGS[viewName] || [];
      const filteredQualityFlags = qualityFlags.filter(selectedFlag =>
        viewQualityFlags.some(viewFlag => viewFlag.code === selectedFlag.code)
      );
      if (filteredQualityFlags.length > 0) {
        u += `&quality_flags=${encodeURIComponent(JSON.stringify(filteredQualityFlags))}`;
      }
    }

    if (isFiltered !== undefined) {
      u += `&is_filtered=${isFiltered}`;
    }
    return u;
  };

  // Keep a plain function for the legacy usePaginated wrapper
  const getPaginatedViewSegmentationsReportFn = async (
    p: number = page,
    signal?: AbortSignal
  ): Promise<any> => {
    const response = await apiClient.get(buildUrl(p), { signal });
    return response;
  };

  const baseQueryKey = [page.toString(), studyUID, viewName];
  const queryKeyParts = [...baseQueryKey];
  
  if (seriesIds && seriesIds.length > 0 && !(seriesIds.length === 1 && seriesIds[0] === 'All')) {
    queryKeyParts.push(seriesIds.sort().join(','));
  }
  
  if (qualityFlags && qualityFlags.length > 0) {
    // Get the quality flags defined for this specific view
    const viewQualityFlags = QUALITY_FLAGS[viewName] || [];
    
    // Filter the selected quality flags to only include those relevant to this view
    const filteredQualityFlags = qualityFlags.filter(selectedFlag => 
      viewQualityFlags.some(viewFlag => viewFlag.code === selectedFlag.code)
    );
    
    // Only add to query key if there are relevant flags for this view
    if (filteredQualityFlags.length > 0) {
      const sortedFlags = filteredQualityFlags
        .map(flag => `${flag.code}:${flag.shouldBeVisible}`)
        .sort()
        .join(',');
      queryKeyParts.push(sortedFlags);
    }
  }
  
  if (isFiltered !== undefined) {
    queryKeyParts.push(`filtered:${isFiltered}`);
  }

  // Use useQuery directly so React Query's AbortSignal is forwarded to the HTTP request.
  // This means when queryClient.cancelQueries() is called the actual network request stops.
  return useQuery({
    queryKey: queryKeyParts,
    queryFn: ({ signal }) => getPaginatedViewSegmentationsReportFn(page, signal),
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

/** Fetch all view definitions (id, view_code, display_name) from the in-memory server cache.
 *  Used to look up view_def_id when assigning frames to "not available" views in the grid. */
export function useGetViewDefinitions() {
  return useQuery({
    queryKey: ['view-definitions'],
    queryFn: async () => {
      const response = await apiClient.get('view-frame/view-definitions');
      return response;
    },
    staleTime: Infinity, // View defs never change at runtime
    gcTime: Infinity,
  });
}

/** Fetch the user-saved view ordering for a study (list of view_code strings). */
export function useGetViewsOrder(studyUID: string | null | undefined) {
  return useQuery({
    queryKey: ['views-order', studyUID],
    queryFn: async () => {
      const response = await apiClient.get(`view-frame/${studyUID}/views/order`);
      return response;
    },
    enabled: !!studyUID,
    staleTime: Infinity, // Order only changes when user explicitly saves it
    gcTime: Infinity,
  });
}

/** Persist the user-defined view ordering for a study to the database. */
export async function saveViewsOrder(studyUID: string, order: string[]): Promise<void> {
  await apiClient.put(`view-frame/${studyUID}/views/order`, { order });
}

/** Fetch a single frame's full data (base64 image + quality flags + segmentations) by frame_id.
 *  Use this for instant direct navigation when clicking a specific frame thumbnail.
 *  Pass viewDefId to scope segmentations/quality flags to one view (report per-view editor). */
export function useGetSingleFrame(
  frameId: string | null | undefined,
  studyUID?: string,
  options: { enabled?: boolean; viewDefId?: number | null } = {}
) {
  const viewDefId = options.viewDefId ?? null;
  return useQuery({
    queryKey: ['single-frame', frameId, studyUID, viewDefId],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      if (studyUID) params.set('study_instance_uid', studyUID);
      if (viewDefId != null) params.set('view_def_id', String(viewDefId));
      const qs = params.toString();
      const url = `view-frame/frame/${frameId}${qs ? `?${qs}` : ''}`;
      const response = await apiClient.get(url, { signal });
      return response;
    },
    enabled: options.enabled !== false && !!frameId,
    staleTime: 30_000,
    gcTime: 60_000,
  });
}

/** Fetch lightweight thumbnail URLs for the best N frames of a view (sorted by quality criteria met).
 *  Does NOT fetch image bytes — just returns URLs for use with LazyThumb. */
export function useGetViewThumbnailUrls(
  studyUID: string | null,
  viewCode: string | null,
  count = 8,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: ['view-thumbnail-urls', studyUID, viewCode, count],
    queryFn: async () => {
      const response = await apiClient.get(
        `view-frame/${studyUID}/views/${viewCode}/thumbnail-urls?count=${count}`
      );
      return response;
    },
    enabled: options.enabled !== false && !!studyUID && !!viewCode,
    staleTime: 60_000,
  });
}

export function useGetReportViews(StudyUID: string, seriesIds?: string[], config = {}) {
  const getReportViews = async () => {
    let url_path = `${url.GET_REPORT_VIEWS}/${StudyUID}/views`;
    
    if (seriesIds && seriesIds.length > 0 && !(seriesIds.length === 1 && seriesIds[0] === 'All')) {
      const seriesParam = seriesIds.join(',');
      url_path += `?series_ids=${encodeURIComponent(seriesParam)}`;
    }
    
    const response = await apiClient.get(url_path);
    return response;
  };

  const baseKey = `report_views/${StudyUID}`;
  const queryKey: string[] = seriesIds && seriesIds.length > 0 && !(seriesIds.length === 1 && seriesIds[0] === 'All')
    ? [baseKey, seriesIds.sort().join(',')]
    : [baseKey];

  return useSingleFetch(
    () => getReportViews(),
    queryKey,
    StudyUID,
    { staleTime: 0, refetchOnMount: 'always', ...config }
  );
}

export function useDeleteFrameView() {
  const deleteFrameView = async (frameViewId: string) => {
    const response = await apiClient.delete(`view-frame/${frameViewId}`);
    return response;
  };

  return { deleteFrameView };
}

export function useUpdateQualityFlag() {
  const updateQualityFlag = async (qualityFlagId: string, value: boolean) => {
    const response = await apiClient.patch(`view-frame/quality-flag/${qualityFlagId}?value=${value}`, null);
    return response;
  };

  const createOrUpdateQualityFlag = async (frameId: string, flagDefId: number, value: boolean) => {
    const response = await apiClient.patch(`view-frame/quality-flag/create-or-update?frame_view_id=${frameId}&flag_def_id=${flagDefId}&value=${value}`, null);
    return response;
  };

  return { updateQualityFlag, createOrUpdateQualityFlag };
}

/** Segmentation payload for save. Match by id (update) or (frame_id + category_id) or (frame_id + label). */
export interface SegmentationInput {
  id?: string;
  /** Source of truth for structure; used with frame_id for matching when label is null */
  category_id?: number | null;
  /** For measurements: MEAS__AC etc. For structures: optional (display from category_id on backend) */
  label?: string | null;
  /** Backend expects geometry_type (UPPERCASE: ELLIPSE, POLYLINE, LINE, FREEHAND, POLYGON) */
  geometry_type: string;
  /** @deprecated Use geometry_type */
  type?: string;
  /** "structure" | "measurement"; backend derives from label if omitted */
  segment_type?: 'structure' | 'measurement';
  points: string; // Format: "x1\\y1\\x2\\y2..."
  area?: number;
  perimeter?: number;
  length?: number;
  score?: number;
}

export interface MeasurementInput {
  meas_name: string; // e.g., "MEAS__HC", "MEAS__BPD", "MEAS__FL", "MEAS__AC"
  value: number; // Value in cm
}

export interface SaveSegmentationsRequest {
  sop_instance_uid: string;
  frame_number: number;
  frame_id: string;
  segmentations: SegmentationInput[];
  measurements?: MeasurementInput[]; // Optional measurements to save
  delete_segmentation_ids?: string[]; // Optional list of segmentation IDs (UUIDs) to delete
  delete_unlisted?: boolean; // If true, delete segmentations not in the list (sync mode)
  [key: string]: unknown; // allow spread into Record<string, unknown>
}

export interface SaveSegmentationsResponse {
  success: boolean;
  saved_ids: string[];
  updated_ids: string[];
  deleted_ids?: string[];
  saved_measurement_ids?: string[];
  updated_measurement_ids?: string[];
  frame_id?: string;
  error?: string;
}

export function useSaveSegmentations() {
  const saveSegmentations = async (request: SaveSegmentationsRequest): Promise<SaveSegmentationsResponse> => {
    const response = await apiClient.post('view-frame/segmentations', request);
    return response.data?.data || response.data;
  };

  return { saveSegmentations };
}

export type ViewAgreement = 'agree' | 'conflict' | 'user_only' | 'ml_only' | 'none';

export interface GeneralViewerFrame {
  frame_number: number | string;
  frame_id: string;
  sop_instance_uid: string;
  serie_instance_uid: string;
  serie_type?: string;
  format_file?: string;
  view_def_id?: number | null;
  view_agreement?: ViewAgreement;
  quality_flags: any[];
  measurements: any[];
  segmentations: any[];
  pixel_spacing?: number[] | null;
  pixel_spacing_unit?: string | null;
  modality?: string;
  global_pixel_spacing?: number[];
  image_url?: string;
}

export interface SegmentationRef {
  seg_id: string;
  is_manual: boolean;
  geometry_type?: string | null;
}

export interface ViewValidationCheck {
  category_id: number;
  display_name: string;
  rule_type: 'visible' | 'not_visible';
  required_for_match: boolean;
  /** Max allowed drawings for this category per frame (1 default, 2 for bilateral structures like kidneys). */
  max_structures: number;
  is_satisfied: boolean;
  has_conflict: boolean;
  /** All segs (user + ML) for this category on the frame. */
  all_segs: SegmentationRef[];
  /** Convenience: single user seg id (null when 0 or >1 user segs). */
  user_seg_id: string | null;
  /** Convenience: single ML seg id (null when 0 or >1 ML segs). */
  ml_seg_id: string | null;
}

export interface ViewValidationResult {
  success: boolean;
  view_def_id: number;
  frame_id: string;
  /** All ML-assigned view IDs for this frame (can belong to multiple views). */
  frame_ml_view_def_ids: number[];
  checks: ViewValidationCheck[];
  all_required_satisfied: boolean;
  conflict_count: number;
}

/** Fetch view quality-rule satisfaction + conflict info for a frame/view pair. */
export function useValidateFrameForView() {
  const validate = async (
    frameId: string,
    viewDefId: number
  ): Promise<ViewValidationResult | null> => {
    try {
      const response = await apiClient.get(
        `view-frame/${frameId}/view-validation/${viewDefId}`
      );
      return response.data?.data || response.data;
    } catch {
      return null;
    }
  };
  return { validate };
}

/** Assign, remove, or clear a user-chosen view on a specific frame (multi-view). */
export function useAssignUserView() {
  const assignUserView = async (
    frameId: string,
    viewDefId: number | null,
    remove = false
  ): Promise<{
    success: boolean;
    frame_id?: string;
    user_view_def_ids?: number[];
    ml_view_def_id?: number | null;
    view_agreement?: ViewAgreement;
    error?: string;
  }> => {
    const response = await apiClient.put(`view-frame/${frameId}/user-view`, { view_def_id: viewDefId, remove });
    return response.data?.data || response.data;
  };

  return { assignUserView };
}

/** General Viewer: instance list item from API */
export interface GeneralViewerInstance {
  sop_instance_uid: string;
  serie_instance_uid: string;
  frame_count: number;
  /** Filtered frame count when filters are active; equals frame_count when no filters. */
  filtered_frame_count?: number;
  first_frame_image_url: string | null;
  format_file?: 'DICOM' | 'VIDEO';
  /** From DB — useful when DICOMweb metadata is missing (e.g. native video in object storage). */
  modality?: string | null;
  sop_class_uid?: string | null;
  fps?: number | null;
  width?: number | null;
  height?: number | null;
}

/** Response from GET /view-frame/video-url for VIDEO instances (frontend loads video once, extracts frames in browser). */
export interface VideoStreamUrlResponse {
  /** Playable URL: same-origin /view-frame/video-stream when playback_token is returned (COEP-safe), else GCS signed URL. */
  url: string;
  fps: number;
  total_frames: number;
}

function buildProxyVideoStreamUrl(sopInstanceUID: string, playbackToken: string): string {
  const base = (axios.defaults.baseURL || '').replace(/\/$/, '');
  const q = new URLSearchParams({ sopInstanceUID, pt: playbackToken });
  return `${base}/view-frame/video-stream?${q.toString()}`;
}

/** Fire-and-forget: ask the backend to pre-generate all thumbnails for a VIDEO instance.
 *  The backend runs a single sequential FFmpeg pass and fills the thumbnail cache progressively.
 *  Call this when entering grid mode — the first frames become available within ~1 s. */
export async function triggerVideoThumbnailWarmup(sopInstanceUID: string): Promise<void> {
  try {
    await apiClient.post(`view-frame/video-warmup-thumbnails?sopInstanceUID=${encodeURIComponent(sopInstanceUID)}`);
  } catch {
    // Warmup is best-effort; on-demand fallback still works
  }
}

/** Fetch signed video URL + metadata for a VIDEO instance. Used so the frontend can load the video once and slice frames in the browser. */
/** DeepEcho DB series-level summary (DICOM metadata panel Study tab when PACS has no DICOMweb). */
export interface AppSeriesMetadata {
  study_instance_uid: string;
  serie_instance_uid: string;
  serie_type: string | null;
  global_pixel_spacing: unknown;
  instance_count: number;
  video_instance_count: number;
  dicom_instance_count: number;
  total_frames_all_instances: number;
  modalities: string[];
  first_video_fps: number | null;
  first_video_width: number | null;
  first_video_height: number | null;
  first_dicom_modality: string | null;
}

export async function fetchAppSeriesMetadata(
  studyUID: string,
  serieUID: string
): Promise<AppSeriesMetadata> {
  const response = await apiClient.get(
    `view-frame/${encodeURIComponent(studyUID)}/series/${encodeURIComponent(serieUID)}/app-metadata`
  );
  const data = (response as any)?.data ?? response;
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid app series metadata response');
  }
  return data as AppSeriesMetadata;
}

export async function getVideoStreamUrl(sopInstanceUID: string): Promise<VideoStreamUrlResponse | null> {
  try {
    const response = await apiClient.get(`view-frame/video-url?sopInstanceUID=${encodeURIComponent(sopInstanceUID)}`);
    const data = (response as any)?.data ?? response;
    if (typeof data.fps !== 'number') {
      return null;
    }
    const playbackToken = data.playback_token;
    const url =
      typeof playbackToken === 'string' && playbackToken.length > 0
        ? buildProxyVideoStreamUrl(sopInstanceUID, playbackToken)
        : typeof data.url === 'string'
          ? data.url
          : null;
    if (!url) {
      return null;
    }
    return {
      url,
      fps: data.fps,
      total_frames: Number(data.total_frames) || 0,
    };
  } catch {
    return null;
  }
}

// Reused canvas for video frame capture to avoid creating 12+ canvases/sec at 12 fps (reduces memory pressure).
let _videoCaptureCanvas: HTMLCanvasElement | null = null;
function getVideoCaptureCanvas(width: number, height: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  if (!_videoCaptureCanvas) {
    _videoCaptureCanvas = document.createElement('canvas');
  }
  if (_videoCaptureCanvas.width !== width || _videoCaptureCanvas.height !== height) {
    _videoCaptureCanvas.width = width;
    _videoCaptureCanvas.height = height;
  }
  const ctx = _videoCaptureCanvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2d not available');
  return { canvas: _videoCaptureCanvas, ctx };
}

/** Capture the video's current frame (no seek) to a blob URL. Use during playback when video is already at the right time. Reuses a single canvas to limit memory. */
export function captureCurrentVideoFrame(video: HTMLVideoElement): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      if (!video.videoWidth || !video.videoHeight) {
        reject(new Error('Video not ready'));
        return;
      }
      const { ctx } = getVideoCaptureCanvas(video.videoWidth, video.videoHeight);
      ctx.drawImage(video, 0, 0);
      const canvas = _videoCaptureCanvas!;
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('toBlob failed'));
            return;
          }
          resolve(URL.createObjectURL(blob));
        },
        'image/jpeg',
        0.7
      );
    } catch (e) {
      reject(e);
    }
  });
}

/** Seek to frame then extract and return a blob URL (JPEG). Use when user scrubs to a frame. */
export function getVideoFrameAsBlobUrl(
  video: HTMLVideoElement,
  frameNumber: number,
  fps: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (fps <= 0) {
      reject(new Error('Invalid fps'));
      return;
    }
    const time = frameNumber / fps;
    const timeout = setTimeout(() => reject(new Error('Video seek timeout')), 6000);

    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
      clearTimeout(timeout);
      try {
        const { ctx } = getVideoCaptureCanvas(video.videoWidth, video.videoHeight);
        ctx.drawImage(video, 0, 0);
        const canvas = _videoCaptureCanvas!;
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('toBlob failed'));
              return;
            }
            resolve(URL.createObjectURL(blob));
          },
          'image/jpeg',
          0.72
        );
      } catch (e) {
        reject(e);
      }
    };

    const onError = () => {
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
      clearTimeout(timeout);
      reject(new Error('Video seek error'));
    };

    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);
    video.currentTime = time;
  });
}

// ── Module-level LRU cache for blob URLs ────────────────────────────────────
// Avoids re-downloading images when the user switches between instances and
// comes back. Entries are keyed by imageUrl (the thumbnail URL path).
// LRU evicts oldest when capacity exceeded, revoking the blob URL automatically.
//
// IMPORTANT: this cap must be ≥ MAX_GRID_BLOBS (component-level cap) so that
// the module cache never revokes a URL that the component is still rendering.
// At 50 KB/frame × 3000 ≈ 150 MB — well within browser limits alongside
// the 200-entry ImageBitmap cap (~200 MB). Total session peak ≈ 350 MB.
const MAX_BLOB_CACHE_ENTRIES = 3000;

class BlobUrlLRU {
  private cache = new Map<string, string>();

  get(key: string): string | undefined {
    const val = this.cache.get(key);
    if (val !== undefined) {
      // Promote to most-recently-used position
      this.cache.delete(key);
      this.cache.set(key, val);
    }
    return val;
  }

  set(key: string, blobUrl: string): void {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= MAX_BLOB_CACHE_ENTRIES) {
      // Evict LRU entry (first item in Map iteration order)
      const lruKey = this.cache.keys().next().value as string;
      const lruUrl = this.cache.get(lruKey)!;
      if (lruUrl.startsWith('blob:')) URL.revokeObjectURL(lruUrl);
      this.cache.delete(lruKey);
    }
    this.cache.set(key, blobUrl);
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }

  /** Return cached blob URL without updating LRU order (read-only peek). */
  peek(key: string): string | undefined {
    return this.cache.get(key);
  }

  /** Remove a single entry by key. Call when the component has already revoked the URL so we don't return it later. */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /** Revoke all blob URLs and clear the cache. Call when leaving the viewer so memory is released. */
  clear(): void {
    this.cache.forEach((url) => {
      if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    });
    this.cache.clear();
  }
}

const blobUrlCache = new BlobUrlLRU();

// ── ImageBitmap cache for zero-copy canvas rendering during high-fps playback ─
// ImageBitmaps are GPU-decoded and can be drawn to a canvas in <1ms without any JS-side copy.
// Populated as a side-effect of fetchFrameImage so bitmaps are ready before the player needs them.
//
// ⚠ Memory: each decoded ImageBitmap occupies raw pixel memory — a typical 512×512 RGBA frame
// uses ~1 MB, and 800×600 ~1.9 MB. Without a size cap a 2000-frame study accumulates 2–4 GB.
// We keep the most-recently-accessed 200 bitmaps (≈200–400 MB) and close the rest immediately.
const MAX_BITMAP_CACHE_ENTRIES = 200;

class ImageBitmapLRU {
  private cache = new Map<string, ImageBitmap>();

  get(key: string): ImageBitmap | undefined {
    const val = this.cache.get(key);
    if (val !== undefined) {
      this.cache.delete(key);
      this.cache.set(key, val);
    }
    return val;
  }

  set(key: string, bm: ImageBitmap): void {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= MAX_BITMAP_CACHE_ENTRIES) {
      const lruKey = this.cache.keys().next().value as string;
      const lruBm = this.cache.get(lruKey)!;
      try { lruBm.close(); } catch {}
      this.cache.delete(lruKey);
    }
    this.cache.set(key, bm);
  }

  has(key: string): boolean { return this.cache.has(key); }

  forEach(cb: (bm: ImageBitmap) => void): void { this.cache.forEach(cb); }

  clear(): void {
    this.cache.forEach((bm) => { try { bm.close(); } catch {} });
    this.cache.clear();
  }
}

const imageBitmapCache = new ImageBitmapLRU();

/** Return a cached GPU-decoded ImageBitmap for the given image URL, or undefined if not yet decoded. */
export function getCachedImageBitmap(imageUrl: string): ImageBitmap | undefined {
  return imageBitmapCache.get(imageUrl);
}

/** Clear the frame image blob cache and revoke all blob URLs. Call when the user leaves the General Viewer
 *  (e.g. navigates to another page) so the browser can release the memory (often hundreds of MB). */
export function clearGeneralViewerFrameCache(): void {
  blobUrlCache.clear();
  imageBitmapCache.clear();
}

/** Return a cached blob URL for imageUrl, if one exists.
 *  Useful for pre-populating component state when switching back to a known instance. */
export function getCachedFrameImageUrl(imageUrl: string): string | undefined {
  return blobUrlCache.peek(imageUrl);
}

/** Remove a frame's blob URL from the module-level cache. Call after revoking the URL in the component
 *  so we don't return a revoked URL when the user scrolls back to that frame. */
export function removeCachedFrameImageUrl(imageUrl: string): void {
  blobUrlCache.delete(imageUrl);
}

/** Fetch a single frame image via the thumbnail endpoint; returns a blob URL string.
 *
 *  Results are cached in a module-level LRU so revisiting a previous instance
 *  is instant — no network request, no memory growth beyond MAX_BLOB_CACHE_ENTRIES.
 *
 *  Uses apiClient.getBlob() — NOT apiClient.get() — because get(url, params) treats
 *  its second argument as URL query params and would append "?responseType=blob" to
 *  the URL, causing FastAPI to receive frameNumber="3?responseType=blob".
 *
 *  Pass signal to cancel the request when the user switches instance (avoids
 *  hundreds of in-flight requests and platform lag).
 */
export async function fetchFrameImage(
  imageUrl: string,
  options?: { signal?: AbortSignal }
): Promise<string> {
  const cached = blobUrlCache.get(imageUrl);
  if (cached) return cached;
  if (options?.signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const result: any = await apiClient.getBlob(imageUrl, {
    signal: options?.signal,
  });
  if (options?.signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  // Interceptor may return Blob directly or leave it wrapped — handle both
  const blob: Blob =
    result instanceof Blob
      ? result
      : result?.data instanceof Blob
        ? result.data
        : new Blob([result?.data ?? result], { type: 'image/jpeg' });
  const blobUrl = URL.createObjectURL(blob);
  if (MAX_BLOB_CACHE_ENTRIES > 0) blobUrlCache.set(imageUrl, blobUrl);

  // Pre-decode to ImageBitmap in the background for zero-copy canvas rendering at high fps.
  // createImageBitmap is GPU-accelerated and non-blocking; the bitmap is ready by the time the
  // player reaches this frame (ring buffer guarantees at least 15 frames of lead time).
  if (!imageBitmapCache.has(imageUrl) && typeof createImageBitmap === 'function') {
    createImageBitmap(blob).then((bm) => { imageBitmapCache.set(imageUrl, bm); }).catch(() => {});
  }

  return blobUrl;
}

/** General Viewer: fetch instances for left panel (optional filters: view_codes, quality_flag_codes, category_groups) */
export function useGetGeneralViewerInstances(
  studyUID: string,
  seriesIds?: string[],
  filters?: { viewCodes?: string[]; qualityFlagCodes?: string[]; categoryGroups?: string[] },
  config: { enabled?: boolean } = {}
) {
  const fetchFn = async () => {
    let url_path = `view-frame/${studyUID}/general-viewer/instances`;
    const params = new URLSearchParams();
    if (seriesIds && seriesIds.length > 0 && seriesIds[0] !== 'All') {
      params.set('series_ids', seriesIds.join(','));
    }
    if (filters?.viewCodes?.length) {
      params.set('view_codes', filters.viewCodes.join(','));
    }
    if (filters?.qualityFlagCodes?.length) {
      params.set('quality_flag_codes', filters.qualityFlagCodes.join(','));
    }
    if (filters?.categoryGroups?.length) {
      params.set('category_groups', filters.categoryGroups.join(','));
    }
    const qs = params.toString();
    if (qs) url_path += `?${qs}`;
    const response = await apiClient.get(url_path);
    return response;
  };

  return useQuery({
    queryKey: viewFrameQueryKeys.generalViewerInstances(studyUID, seriesIds, filters),
    queryFn: fetchFn,
    enabled: !!studyUID && (config.enabled !== false),
    // Refetch when component mounts (e.g. after returning from Views tab) so instance list is fresh
    refetchOnMount: 'always',
  });
}

const DEFAULT_GENERAL_VIEWER_PAGE_SIZE = 40;

/** General Viewer: fetch frames for one instance (optional filters). When page/pageSize provided, returns that page; otherwise legacy single fetch (all frames). */
export function useGetInstanceFramesForGeneralViewer(
  studyUID: string,
  sopInstanceUID: string | null,
  filters?: { viewCodes?: string[]; qualityFlagCodes?: string[]; categoryGroups?: string[] },
  config: { enabled?: boolean } = {}
) {
  const fetchFn = async () => {
    if (!sopInstanceUID) return { data: { frames: [], pagination: {} } };
    let url_path = `view-frame/${studyUID}/general-viewer/instances/${sopInstanceUID}/frames`;
    const params = new URLSearchParams();
    if (filters?.viewCodes?.length) {
      params.set('view_codes', filters.viewCodes.join(','));
    }
    if (filters?.qualityFlagCodes?.length) {
      params.set('quality_flag_codes', filters.qualityFlagCodes.join(','));
    }
    if (filters?.categoryGroups?.length) {
      params.set('category_groups', filters.categoryGroups.join(','));
    }
    const qs = params.toString();
    if (qs) url_path += `?${qs}`;
    const response = await apiClient.get(url_path);
    return response;
  };

  return useQuery({
    queryKey: viewFrameQueryKeys.generalViewerInstanceFrames(studyUID, sopInstanceUID || '', filters),
    queryFn: fetchFn,
    enabled: !!studyUID && !!sopInstanceUID && (config.enabled !== false),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/** General Viewer: fetch ALL frame metadata (no images) for one instance in a single fast request.
 *  Each frame has image_url instead of image_base64.
 *  Use this as the initial load; then progressively load images with fetchFrameImage(). */
export function useGetInstanceFramesMetadata(
  studyUID: string,
  sopInstanceUID: string | null,
  filters?: { viewCodes?: string[]; qualityFlagCodes?: string[]; categoryGroups?: string[] },
  config: { enabled?: boolean } = {}
) {
  const fetchFn = async () => {
    if (!sopInstanceUID) return { data: { frames: [], pagination: {} } };
    const params = new URLSearchParams({ metadata_only: 'true' });
    if (filters?.viewCodes?.length) params.set('view_codes', filters.viewCodes.join(','));
    if (filters?.qualityFlagCodes?.length) params.set('quality_flag_codes', filters.qualityFlagCodes.join(','));
    if (filters?.categoryGroups?.length) params.set('category_groups', filters.categoryGroups.join(','));
    const url_path = `view-frame/${studyUID}/general-viewer/instances/${sopInstanceUID}/frames?${params.toString()}`;
    const response = await apiClient.get(url_path);
    return response;
  };

  return useQuery({
    queryKey: [...viewFrameQueryKeys.generalViewerInstanceFrames(studyUID, sopInstanceUID || '', filters), 'metadata'],
    queryFn: fetchFn,
    enabled: !!studyUID && !!sopInstanceUID && config.enabled !== false,
    // 5-minute window: switching back to a recently-viewed instance reuses cached
    // metadata instead of re-fetching. After 5 min (or after a save/refetch) the
    // data is considered stale and refreshed automatically.
    staleTime: 5 * 60 * 1000,
  });
}

/** General Viewer: paginated frames for one instance (page, pageSize, filters). Use this and merge pages in component state. */
export function usePaginatedInstanceFramesForGeneralViewer(
  studyUID: string,
  sopInstanceUID: string | null,
  page: number,
  pageSize: number = DEFAULT_GENERAL_VIEWER_PAGE_SIZE,
  filters?: { viewCodes?: string[]; qualityFlagCodes?: string[]; categoryGroups?: string[] },
  config: { enabled?: boolean } = {}
): UseQueryResult<{ data?: { data?: { frames?: any[]; pagination?: { page?: number; pageSize?: number; total?: number; totalPages?: number; hasMore?: boolean } }; frames?: any[]; pagination?: { page?: number; pageSize?: number; total?: number; totalPages?: number; hasMore?: boolean } } }> {
  const getPageFn = async (p: number) => {
    if (!sopInstanceUID) return { data: { frames: [], pagination: { page: 1, pageSize: 0, total: 0, totalPages: 0, hasMore: false } } };
    const params = new URLSearchParams();
    params.set('page', String(p));
    params.set('page_size', String(pageSize));
    if (filters?.viewCodes?.length) {
      params.set('view_codes', filters.viewCodes.join(','));
    }
    if (filters?.qualityFlagCodes?.length) {
      params.set('quality_flag_codes', filters.qualityFlagCodes.join(','));
    }
    if (filters?.categoryGroups?.length) {
      params.set('category_groups', filters.categoryGroups.join(','));
    }
    const url_path = `view-frame/${studyUID}/general-viewer/instances/${sopInstanceUID}/frames?${params.toString()}`;
    const response = await apiClient.get(url_path);
    return response;
  };

  const queryKey = [...viewFrameQueryKeys.generalViewerInstanceFrames(studyUID, sopInstanceUID || '', filters, page, pageSize)].map(String);
  return usePaginated(getPageFn, queryKey, page, {
    staleTime: 0,
    enabled: !!studyUID && !!sopInstanceUID && (config.enabled !== false),
  });
}
